import React, { useSyncExternalStore } from 'react';
import { useDialog } from '../hooks/useDialog';
import { appConfig } from '../config';
import { applyUpdateNow, getUpdateInputs, isNewerVersion } from '../utils/updateStatus';
import { dismissSelfHelp, getSelfHelpOffer, snoozeSelfHelp, subscribeSelfHelp, type SelfHelp } from '../utils/selfHelp';

/**
 * Tier 1 self-help copy. Steve's rule: say first that something went wrong, then why we're
 * asking, then that their work is safe — or people ignore the button. Plain words only.
 */
export const SELF_HELP_COPY: Record<SelfHelp, { heading: string; reason: string; safe: string; action: string }> = {
  update: {
    heading: 'We noticed a problem with the app',
    reason: "Something went wrong just now. You're using an older copy of the app, and the newest one already fixes this.",
    safe: 'Your work is saved. Updating takes a few seconds.',
    action: 'Update now',
  },
  signin: {
    heading: 'We found an error',
    reason: "The app lost track of who you are, so it can't save your changes right now. Signing out and back in fixes it.",
    // Saved changes wait on this phone in the person's name and go through after they sign back
    // in (persistent Firestore cache, not cleared on sign-out). Text still in a box is not saved.
    safe: 'Anything you already saved stays on this phone and goes through after you sign back in.',
    action: 'Sign out and back in',
  },
};

const stillOld = () => isNewerVersion(getUpdateInputs().liveVersion, appConfig.version);
const snooze = () => snoozeSelfHelp(Date.now(), stillOld);

const Sheet: React.FC<{ kind: SelfHelp; onSignOut: () => void }> = ({ kind, onSignOut }) => {
  const copy = SELF_HELP_COPY[kind];
  const close = kind === 'update' ? snooze : dismissSelfHelp;
  const dialog = useDialog(close);
  const act = () => {
    if (kind === 'update') {
      applyUpdateNow();
    } else {
      dismissSelfHelp();
      onSignOut();
    }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-end justify-center" {...dialog.backdropProps}>
      <div
        {...dialog.props}
        aria-labelledby={dialog.titleId}
        className="w-full max-w-lg bg-white dark:bg-slate-800 rounded-t-2xl p-5 space-y-3"
        style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 20px)' }}
      >
        <h2 id={dialog.titleId} className="font-bold text-lg text-slate-900 dark:text-slate-100">{copy.heading}</h2>
        <p className="text-slate-700 dark:text-slate-300">{copy.reason}</p>
        <p className="text-sm rounded-lg px-3 py-2 bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200">✓ {copy.safe}</p>
        <button type="button" onClick={act} className="w-full min-h-[48px] rounded-xl bg-brand-go text-brand-navy dark:text-brand-navy font-bold">
          {copy.action}
        </button>
        {kind === 'update' && (
          <button type="button" onClick={snooze} className="w-full min-h-[44px] rounded-xl font-semibold text-slate-600 dark:text-slate-300">
            Remind me in a few minutes
          </button>
        )}
      </div>
    </div>
  );
};

/** The fix-it pop-up. Mounted once in App; shows whatever logError last offered. */
export const SelfHelpSheet: React.FC<{ onSignOut: () => void }> = ({ onSignOut }) => {
  const offer = useSyncExternalStore(subscribeSelfHelp, getSelfHelpOffer, getSelfHelpOffer);
  return offer ? <Sheet kind={offer} onSignOut={onSignOut} /> : null;
};
